import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { getUrlWithSelectedPropertyId } from '../../utils/navigation-helpers';
import messages from './messages';
import { TAB_KEYS } from '../ResidentProfile/constants';

const NO_PREFERENCE_OPTIONS = [];

const formatPreferences = (preferences, enabledPreferenceIds) =>
  preferences
    ? Object.entries(preferences)
        .filter(([key, value]) => value && enabledPreferenceIds.has(key))
        .map(([key]) =>
          key
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/^./, (char) => char.toUpperCase()),
        )
        .join(', ')
    : '';

const useRows = ({
  positions,
  propertyId,
  openSelectFromWaitlist,
  openDeny,
  openCancel,
  openReinstate,
  preferenceOptions = NO_PREFERENCE_OPTIONS,
}) => {
  return useMemo(() => {
    const enabledPreferenceIds = new Set(
      preferenceOptions.map(({ value }) => value),
    );

    return (positions ?? []).map((position) => {
      const { affordableWaitlist } = position;
      const { application } = affordableWaitlist;
      const applicationStatusName = application.applicationStatus?.name;
      const isWaitlistActive = applicationStatusName === 'Waitlist';
      const isDeniedOrCanceled =
        applicationStatusName === 'Denied' ||
        applicationStatusName === 'Canceled';

      return {
        id: position.id,
        position: {
          variant: 'number',
          value: position.rank,
        },
        preferenceWeight: {
          variant: 'number',
          value: position.preferenceWeight,
        },
        applicationDateTime: {
          variant: 'dateTime',
          value: affordableWaitlist.applicationDateTime,
        },
        affordableProgram: {
          value: affordableWaitlist.affordableProgram,
        },
        applicationCode: {
          variant: 'number',
          value: application.applicationCode,
        },
        applicants: {
          variant: 'link',
          value: application.applicants,
          to: getUrlWithSelectedPropertyId(
            `/application/${affordableWaitlist.applicationId}`,
          ),
          target: '_blank',
        },
        texting: {
          variant: 'texting',
          value: { ...position.texting },
          to: `/property/${propertyId}/application/${affordableWaitlist.applicationId}?tab=${TAB_KEYS.TEXTING}`,
        },
        householdStatus: {
          value: application.householdStatus,
        },
        status: {
          value: applicationStatusName,
        },
        phoneNumber: {
          variant: 'phoneNumber',
          value: application.phoneNumber,
        },
        emailAddress: {
          variant: 'email',
          value: application.emailAddress,
        },
        desiredMoveInDate: {
          variant: 'date',
          value: application.desiredMoveInStartDate,
        },
        applicationReceived: {
          variant: 'dateTime',
          value: application.applicationReceived,
        },
        applicationRemoved: {
          variant: 'dateTime',
          value: application.applicationRemoved,
        },
        preferredUnitSize: {
          value: application.preferredUnitSize,
        },
        householdSize: {
          variant: 'number',
          value: application.householdSize,
        },
        incomeLevel: {
          value: affordableWaitlist.incomeLevel,
        },
        datesContacted: {
          variant: 'date',
          value: application.datesContacted,
        },
        moveInDate: {
          variant: 'date',
          value: application.moveInDate,
        },
        needsUnitAccessible: {
          value: application.needsUnitAccessible ? 'Yes' : 'No',
        },
        requestingAccommodation: {
          value: affordableWaitlist.requestingAccommodation ? 'Yes' : 'No',
        },
        preferences: {
          value: formatPreferences(
            affordableWaitlist.preferences,
            enabledPreferenceIds,
          ),
        },
        assignedTo: {
          value: application.assigneeName,
        },
        actions: {
          variant: 'menu',
          name: 'actionsMenu',
          iconName: 'BoltIcon',
          color: 'primary',
          closeOnClick: true,
          options: [
            ...(isWaitlistActive
              ? [
                  {
                    value: 'selectFromWaitlist',
                    text: <FormattedMessage {...messages.selectFromWaitlist} />,
                    onClick: () => {
                      openSelectFromWaitlist({
                        affordableWaitlistId: position.affordableWaitlistId,
                        affordableWaitlistRow: {
                          id: position.id,
                          rank: position.rank,
                          applicationId: affordableWaitlist.applicationId,
                          bedroomCount: position.bedroomCount,
                        },
                      });
                    },
                  },
                  {
                    value: 'deny',
                    text: <FormattedMessage {...messages.deny} />,
                    onClick: () => {
                      openDeny({
                        affordableWaitlistId: position.affordableWaitlistId,
                      });
                    },
                  },
                  {
                    value: 'cancel',
                    text: <FormattedMessage {...messages.cancel} />,
                    onClick: () => {
                      openCancel({
                        affordableWaitlistId: position.affordableWaitlistId,
                      });
                    },
                  },
                ]
              : []),
            ...(isDeniedOrCanceled
              ? [
                  {
                    value: 'reinstate',
                    text: <FormattedMessage {...messages.reinstate} />,
                    onClick: () => {
                      openReinstate({
                        affordableWaitlistId: position.affordableWaitlistId,
                        applicationId: affordableWaitlist.applicationId,
                      });
                    },
                  },
                ]
              : []),
          ],
        },
      };
    });
  }, [
    positions,
    propertyId,
    openSelectFromWaitlist,
    openDeny,
    openCancel,
    openReinstate,
    preferenceOptions,
  ]);
};

export default useRows;
